import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Group } from '@/types';
import { cn } from '@/lib/utils';

interface GroupHeaderProps {
  group: Group;
  onJoin?: () => void;
  onLeave?: () => void;
  isNotificationsMuted?: boolean;
  onMuteNotifications?: () => void;
  onUnmuteNotifications?: () => void;
}

export function GroupHeader({ group, onJoin, onLeave, isNotificationsMuted, onMuteNotifications, onUnmuteNotifications }: GroupHeaderProps) {
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const isMember = group.myRole != null &&
    (group.myStatus === 'active' || group.myStatus === 'muted');
  const isOwner = group.myRole === 'owner';
  const isModerator = group.myRole === 'moderator';

  return (
    <div className="bg-card border-b border-border">
      {/* Color banner */}
      <div
        className="h-20"
        style={{ backgroundColor: group.color || '#7c3aed' }}
      />

      <div className="px-4 pb-4">
        {/* Group name and actions */}
        <div className="flex items-start justify-between -mt-4">
          <div className="flex-1 min-w-0 pt-6">
            <h1 className="text-xl font-bold text-card-foreground">{group.name}</h1>
            {group.description && (
              <p className="text-sm text-muted-foreground mt-1">{group.description}</p>
            )}
          </div>

          <div className="flex items-center gap-2 pt-2">
            {isMember ? (
              <>
                {(isOwner || isModerator) && (
                  <Link
                    to={`/groups/${group.id}/settings`}
                    className="p-2 rounded-full hover:bg-muted transition-colors"
                    title="Settings"
                  >
                    <SettingsIcon className="w-5 h-5 text-muted-foreground" />
                  </Link>
                )}
                <Link
                  to={`/groups/${group.id}/members`}
                  className="p-2 rounded-full hover:bg-muted transition-colors"
                  title="Members"
                >
                  <UsersIcon className="w-5 h-5 text-muted-foreground" />
                </Link>
                {/* Notification mute toggle */}
                <button
                  onClick={isNotificationsMuted ? onUnmuteNotifications : onMuteNotifications}
                  className="p-2 rounded-full hover:bg-muted transition-colors"
                  title={isNotificationsMuted ? 'Unmute notifications' : 'Mute notifications'}
                >
                  {isNotificationsMuted ? (
                    <BellOffIcon className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <BellIcon className="w-5 h-5 text-muted-foreground" />
                  )}
                </button>
                {!isOwner && (
                  <div className="relative">
                    <button
                      onClick={() => setShowLeaveConfirm(true)}
                      className="text-sm px-4 py-2 rounded-full border border-border text-muted-foreground hover:text-destructive hover:border-destructive transition-colors"
                    >
                      Leave
                    </button>
                    {showLeaveConfirm && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowLeaveConfirm(false)} />
                        <div className="absolute right-0 top-12 z-20 w-56 bg-card border border-border rounded-lg shadow-lg p-3">
                          <p className="text-sm text-card-foreground mb-3">Leave this group?</p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setShowLeaveConfirm(false)}
                              className="flex-1 text-sm px-3 py-1.5 rounded border border-border text-muted-foreground hover:bg-muted transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => { setShowLeaveConfirm(false); onLeave?.(); }}
                              className="flex-1 text-sm px-3 py-1.5 rounded bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
                            >
                              Leave
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            ) : (
              <button
                onClick={onJoin}
                className="text-sm px-4 py-2 rounded-full bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
              >
                Join Group
              </button>
            )}
          </div>
        </div>

        {/* Hashtags */}
        {group.hashtags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {group.hashtags.map((tag) => (
              <span
                key={tag}
                className="px-2.5 py-1 bg-secondary text-secondary-foreground text-xs rounded-full font-medium"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Stats row */}
        <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
          <Link
            to={`/groups/${group.id}/members`}
            className="hover:text-primary transition-colors"
          >
            <span className="font-medium text-card-foreground">{group.memberCount}</span>{' '}
            {group.memberCount === 1 ? 'member' : 'members'}
          </Link>
          <span
            className={cn(
              'px-2 py-0.5 rounded-full text-xs',
              group.visibility === 'private'
                ? 'bg-yellow-500/10 text-yellow-600'
                : 'bg-green-500/10 text-green-600'
            )}
          >
            {group.visibility === 'private' ? 'Private' : 'Public'}
          </span>
          {group.owner && (
            <span>
              by{' '}
              <Link
                to={`/profile/${group.ownerId}`}
                className="hover:text-primary transition-colors"
              >
                {group.owner.displayName}
              </Link>
            </span>
          )}
          {isOwner && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary font-medium">
              Owner
            </span>
          )}
          {isModerator && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-blue-500/10 text-blue-600 font-medium">
              Moderator
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function BellIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  );
}

function BellOffIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      <line x1="3" y1="3" x2="21" y2="21" strokeLinecap="round" />
    </svg>
  );
}
